"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  DEFAULT_RECORDING_DEMO_PRESET_ID,
  getRecordingDemoPreset,
  recordingDemoCellValue,
  type RecordingDemoPreset,
  type RecordingDemoPresetId,
} from "./recording-demo-presets";
import shippingFixture from "./data/shipping-platform.json";
import {
  DEMO_ENVIRONMENT_RESET_EVENT,
  RECORDING_DEMO_STORAGE_KEY,
} from "@/lib/demo-environment";

export type RecordingDemoFileStatus =
  | "uploading"
  | "queued"
  | "processing"
  | "completed";

export type RecordingDemoFile = {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  status: RecordingDemoFileStatus;
  uploadProgress: number;
};

export type RecordingApiKey = {
  id: string;
  name: string | null;
  keyPrefix: string;
  secret: string;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
};

type RecordingDemoCell = string | number | null;
export type RecordingDemoRow = Record<string, RecordingDemoCell> & {
  id: number;
  source_document: string;
};

type RecordingDemoTableData = {
  name: string;
  columns: string[];
  rows: RecordingDemoRow[];
};

type GeneratedFixtureTable = {
  rows: Array<Record<string, RecordingDemoCell>>;
};

const generatedShippingFixture = shippingFixture as Record<
  string,
  GeneratedFixtureTable
>;

type PersistedState = {
  environmentId: RecordingDemoPresetId;
  schemaBuilderMode: boolean;
  schemaCreated: boolean;
  files: RecordingDemoFile[];
  rowsByTable: Record<string, RecordingDemoRow[]>;
  customColumns: Record<string, string[]>;
  apiKeys: RecordingApiKey[];
  instantModeThreshold: number;
  projectName: string;
  tableNames: Record<string, string>;
};

type RecordingDemoContextValue = PersistedState & {
  preset: RecordingDemoPreset;
  isProcessing: boolean;
  processingProgress: number;
  projectName: string;
  tableNames: Record<string, string>;
  tables: Array<{ id: string; name: string; slug: string }>;
  uploadFiles: (files: File[]) => void;
  processFiles: (fileIds?: string[]) => void;
  deleteFiles: (fileIds: string[]) => void;
  getTableData: (tableSlug: string) => RecordingDemoTableData | null;
  addField: (tableSlug: string, fieldName: string) => void;
  updateCell: (
    tableSlug: string,
    rowIndex: number,
    columnName: string,
    value: RecordingDemoCell,
  ) => void;
  createApiKey: (
    name: string | null,
    expiresAt?: string | null,
  ) => RecordingApiKey;
  revokeApiKey: (id: string) => void;
  rotateApiKey: (id: string) => RecordingApiKey | null;
  setProcessingThreshold: (value: number) => void;
  renameProject: (name: string) => void;
  renameTable: (tableSlug: string, name: string) => void;
  changeEnvironment: (environmentId: RecordingDemoPresetId) => void;
  setSchemaBuilderMode: (enabled: boolean) => void;
  createEmptyMockSchema: () => void;
  clearWorkspace: () => void;
  reset: () => void;
  downloadCsv: (tableSlug: string) => void;
};

const METADATA_EVENT = "pdfparse-recording-demo-metadata";
const defaultPreset = getRecordingDemoPreset(DEFAULT_RECORDING_DEMO_PRESET_ID);
const defaultTableNames = (preset: RecordingDemoPreset) =>
  Object.fromEntries(preset.tables.map((entry) => [entry.slug, entry.name]));
const INITIAL_API_KEYS: RecordingApiKey[] = [
  {
    id: "recording-key-1",
    name: "Recording key",
    keyPrefix: "pp_demo_4f2a",
    secret: "pp_demo_4f2a_shipping_platform_recording",
    createdAt: "2026-07-13T12:00:00.000Z",
    expiresAt: null,
    revokedAt: null,
    lastUsedAt: null,
  },
];
const RecordingDemoContext = createContext<RecordingDemoContextValue | null>(
  null,
);

function maxRowId(rows: RecordingDemoRow[] | undefined): number {
  return (rows ?? []).reduce((highest, row) => Math.max(highest, row.id), 0);
}

function buildFixtureRun(
  preset: RecordingDemoPreset,
  sourceDocuments: string[],
  existingRows: Record<string, RecordingDemoRow[]> = {},
) {
  const generated: Record<string, RecordingDemoRow[]> = Object.fromEntries(
    preset.tables.map((entry) => [entry.slug, []]),
  );

  for (const sourceDocument of sourceDocuments) {
    const offsets = Object.fromEntries(
      preset.tables.map((entry) => [
        entry.slug,
        maxRowId([
          ...(existingRows[entry.slug] ?? []),
          ...(generated[entry.slug] ?? []),
        ]),
      ]),
    );

    for (const entry of preset.tables) {
      const generatedFixtureTable =
        preset.id === "document-parser"
          ? generatedShippingFixture[entry.slug]
          : undefined;
      const parent = entry.relationship
        ? preset.tables.find(
            (candidate) =>
              candidate.slug === entry.relationship?.parentTableSlug,
          )
        : undefined;
      const parentOffset = parent ? (offsets[parent.slug] ?? 0) : 0;
      const rows = Array.from({ length: entry.rowCount }, (_, rowIndex) => {
        const generatedFixtureRow =
          generatedFixtureTable?.rows[
            rowIndex % generatedFixtureTable.rows.length
          ];
        const row: RecordingDemoRow = {
          id: (offsets[entry.slug] ?? 0) + rowIndex + 1,
          source_document: sourceDocument,
        };
        for (const column of entry.columns) {
          if (column === entry.relationship?.column && parent) {
            row[column] = generatedFixtureRow?.[column]
              ? parentOffset + Number(generatedFixtureRow[column])
              : parentOffset + (rowIndex % Math.max(parent.rowCount, 1)) + 1;
          } else {
            row[column] =
              generatedFixtureRow?.[column] ??
              recordingDemoCellValue(column, rowIndex, preset.id);
          }
        }
        return row;
      });
      generated[entry.slug]?.push(...rows);
    }
  }

  return generated;
}

export function RecordingDataAdapterProvider({
  children,
  preloadFixture = false,
  persist = true,
}: {
  children: ReactNode;
  preloadFixture?: boolean;
  persist?: boolean;
}) {
  const [files, setFiles] = useState<RecordingDemoFile[]>([]);
  const [environmentId, setEnvironmentId] = useState<RecordingDemoPresetId>(
    DEFAULT_RECORDING_DEMO_PRESET_ID,
  );
  const [schemaBuilderMode, setSchemaBuilderModeState] = useState(
    !preloadFixture,
  );
  const [schemaCreated, setSchemaCreated] = useState(preloadFixture);
  const preset = getRecordingDemoPreset(environmentId);
  const [rowsByTable, setRowsByTable] = useState<
    Record<string, RecordingDemoRow[]>
  >(() =>
    preloadFixture
      ? buildFixtureRun(defaultPreset, ["shipping-document.pdf"])
      : {},
  );
  const [customColumns, setCustomColumns] = useState<Record<string, string[]>>(
    {},
  );
  const [apiKeys, setApiKeys] = useState<RecordingApiKey[]>(INITIAL_API_KEYS);
  const [instantModeThreshold, setInstantModeThreshold] = useState(20);
  const [projectName, setProjectName] = useState(defaultPreset.projectName);
  const [tableNames, setTableNames] = useState<Record<string, string>>(
    defaultTableNames(defaultPreset),
  );
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);
  const timerRefs = useRef<number[]>([]);
  const uploadTimerRefs = useRef<number[]>([]);

  const clearProcessingTimers = useCallback(() => {
    for (const timer of timerRefs.current) window.clearTimeout(timer);
    timerRefs.current = [];
  }, []);
  const clearUploadTimers = useCallback(() => {
    for (const timer of uploadTimerRefs.current) window.clearTimeout(timer);
    uploadTimerRefs.current = [];
  }, []);

  useEffect(() => {
    if (!persist) {
      setIsHydrated(true);
      return;
    }
    try {
      const persisted = window.localStorage.getItem(RECORDING_DEMO_STORAGE_KEY);
      if (persisted) {
        const state = JSON.parse(persisted) as PersistedState;
        const persistedPreset = getRecordingDemoPreset(state.environmentId);
        setEnvironmentId(persistedPreset.id);
        setSchemaBuilderModeState(state.schemaBuilderMode ?? false);
        setSchemaCreated(
          state.schemaBuilderMode ? (state.schemaCreated ?? false) : true,
        );
        setFiles(
          state.files.map((file) => ({
            ...file,
            status:
              file.status === "processing" || file.status === "uploading"
                ? ("queued" as const)
                : file.status,
            uploadProgress: 100,
          })),
        );
        setRowsByTable(state.rowsByTable ?? {});
        setCustomColumns(state.customColumns ?? {});
        setApiKeys(state.apiKeys ?? INITIAL_API_KEYS);
        setInstantModeThreshold(state.instantModeThreshold ?? 20);
        setProjectName(
          state.projectName?.trim() || persistedPreset.projectName,
        );
        setTableNames({
          ...defaultTableNames(persistedPreset),
          ...(state.tableNames ?? {}),
        });
      } else {
        setFiles([]);
        setRowsByTable({});
        setCustomColumns({});
        setApiKeys(INITIAL_API_KEYS);
        setInstantModeThreshold(20);
        setEnvironmentId(DEFAULT_RECORDING_DEMO_PRESET_ID);
        setSchemaBuilderModeState(true);
        setSchemaCreated(false);
        setProjectName(defaultPreset.projectName);
        setTableNames(defaultTableNames(defaultPreset));
      }
    } catch {
      window.localStorage.removeItem(RECORDING_DEMO_STORAGE_KEY);
    } finally {
      setIsHydrated(true);
    }

    return () => {
      clearProcessingTimers();
      clearUploadTimers();
    };
  }, [clearProcessingTimers, clearUploadTimers, persist]);

  useEffect(() => {
    if (!isHydrated || !persist) return;
    window.localStorage.setItem(
      RECORDING_DEMO_STORAGE_KEY,
      JSON.stringify({
        environmentId,
        schemaBuilderMode,
        schemaCreated,
        files,
        rowsByTable,
        customColumns,
        apiKeys,
        instantModeThreshold,
        projectName,
        tableNames,
      }),
    );
    window.dispatchEvent(new Event(METADATA_EVENT));
  }, [
    apiKeys,
    customColumns,
    environmentId,
    files,
    instantModeThreshold,
    isHydrated,
    persist,
    projectName,
    rowsByTable,
    schemaBuilderMode,
    schemaCreated,
    tableNames,
  ]);

  const uploadFiles = useCallback((selectedFiles: File[]) => {
    const now = new Date().toISOString();
    const uploadedFiles = selectedFiles.map((file, index) => ({
      id: `recording-file-${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      uploadedAt: now,
      status: "uploading" as const,
      uploadProgress: 8,
    }));
    setFiles((current) => [...current, ...uploadedFiles]);

    const uploadedIds = new Set(uploadedFiles.map((file) => file.id));
    const updateProgress = (uploadProgress: number) =>
      setFiles((current) =>
        current.map((file) =>
          uploadedIds.has(file.id) ? { ...file, uploadProgress } : file,
        ),
      );

    uploadTimerRefs.current.push(
      window.setTimeout(() => updateProgress(38), 220),
      window.setTimeout(() => updateProgress(76), 520),
      window.setTimeout(() => {
        setFiles((current) =>
          current.map((file) =>
            uploadedIds.has(file.id)
              ? {
                  ...file,
                  status: "queued" as const,
                  uploadProgress: 100,
                }
              : file,
          ),
        );
      }, 850),
    );
  }, []);

  const processFiles = useCallback(
    (fileIds?: string[]) => {
      if (files.some((file) => file.status === "processing")) return;
      const ids = new Set(
        fileIds?.length
          ? fileIds.filter(
              (id) =>
                files.find((file) => file.id === id)?.status !== "uploading",
            )
          : files
              .filter((file) => file.status === "queued")
              .map((file) => file.id),
      );
      if (ids.size === 0) return;
      const selectedFiles = files.filter((file) => ids.has(file.id));
      if (selectedFiles.length === 0) return;

      let virtualRows = rowsByTable;
      const documentRuns = selectedFiles.map((file) => {
        const generatedRows = buildFixtureRun(preset, [file.name], virtualRows);
        virtualRows = Object.fromEntries(
          preset.tables.map((entry) => [
            entry.slug,
            [
              ...(virtualRows[entry.slug] ?? []),
              ...(generatedRows[entry.slug] ?? []),
            ],
          ]),
        );
        return { file, generatedRows };
      });

      const appendRows = (rowsToAppend: Record<string, RecordingDemoRow[]>) =>
        setRowsByTable((current) =>
          Object.fromEntries(
            preset.tables.map((entry) => [
              entry.slug,
              [
                ...(current[entry.slug] ?? []),
                ...(rowsToAppend[entry.slug] ?? []),
              ],
            ]),
          ),
        );

      clearProcessingTimers();
      setProcessingProgress(5);
      setFiles((current) =>
        current.map((file) =>
          ids.has(file.id)
            ? {
                ...file,
                status:
                  file.id === selectedFiles[0]?.id
                    ? ("processing" as const)
                    : ("queued" as const),
              }
            : file,
        ),
      );

      const timers: number[] = [];
      const durationPerDocument = 3000;
      const overallProgress = (
        documentIndex: number,
        documentProgress: number,
      ) =>
        Math.round(
          ((documentIndex + documentProgress / 100) / documentRuns.length) *
            100,
        );

      documentRuns.forEach(({ file, generatedRows }, documentIndex) => {
        const startsAt = documentIndex * durationPerDocument;
        const firstCut = Object.fromEntries(
          Object.entries(generatedRows).map(([slug, rows]) => [
            slug,
            rows.slice(0, Math.ceil(rows.length * 0.2)),
          ]),
        );
        const secondCut = Object.fromEntries(
          Object.entries(generatedRows).map(([slug, rows]) => [
            slug,
            rows.slice(
              Math.ceil(rows.length * 0.2),
              Math.ceil(rows.length * 0.6),
            ),
          ]),
        );
        const finalCut = Object.fromEntries(
          Object.entries(generatedRows).map(([slug, rows]) => [
            slug,
            rows.slice(Math.ceil(rows.length * 0.6)),
          ]),
        );

        timers.push(
          window.setTimeout(() => {
            setProcessingProgress(overallProgress(documentIndex, 20));
          }, startsAt + 600),
          window.setTimeout(() => {
            appendRows(firstCut);
            setProcessingProgress(overallProgress(documentIndex, 40));
          }, startsAt + 1100),
          window.setTimeout(() => {
            appendRows(secondCut);
            setProcessingProgress(overallProgress(documentIndex, 68));
          }, startsAt + 1900),
          window.setTimeout(() => {
            appendRows(finalCut);
            setProcessingProgress(overallProgress(documentIndex, 90));
          }, startsAt + 2600),
          window.setTimeout(() => {
            const nextFile = documentRuns[documentIndex + 1]?.file;
            setFiles((current) =>
              current.map((currentFile) => {
                if (currentFile.id === file.id) {
                  return { ...currentFile, status: "completed" as const };
                }
                if (currentFile.id === nextFile?.id) {
                  return { ...currentFile, status: "processing" as const };
                }
                return currentFile;
              }),
            );
            setProcessingProgress(overallProgress(documentIndex, 100));
            if (!nextFile) timerRefs.current = [];
          }, startsAt + durationPerDocument),
        );
      });
      timerRefs.current = timers;
    },
    [clearProcessingTimers, files, preset, rowsByTable],
  );

  const deleteFiles = useCallback((fileIds: string[]) => {
    const ids = new Set(fileIds);
    setFiles((current) => current.filter((file) => !ids.has(file.id)));
  }, []);

  const getTableData = useCallback(
    (tableSlug: string): RecordingDemoTableData | null => {
      const table = preset.tables.find((entry) => entry.slug === tableSlug);
      if (!table) return null;
      return {
        name: tableNames[tableSlug] ?? table.name,
        columns: [
          "source_document",
          ...table.columns,
          ...(customColumns[tableSlug] ?? []),
        ],
        rows: rowsByTable[tableSlug] ?? [],
      };
    },
    [customColumns, preset.tables, rowsByTable, tableNames],
  );

  const addField = useCallback((tableSlug: string, fieldName: string) => {
    const normalized = fieldName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    if (!normalized) return;
    setCustomColumns((current) => {
      const columns = current[tableSlug] ?? [];
      if (columns.includes(normalized)) return current;
      return { ...current, [tableSlug]: [...columns, normalized] };
    });
  }, []);

  const updateCell = useCallback(
    (
      tableSlug: string,
      rowIndex: number,
      columnName: string,
      value: RecordingDemoCell,
    ) => {
      setRowsByTable((current) => ({
        ...current,
        [tableSlug]: (current[tableSlug] ?? []).map((row, index) =>
          index === rowIndex ? { ...row, [columnName]: value } : row,
        ),
      }));
    },
    [],
  );

  const createApiKey = useCallback(
    (name: string | null, expiresAt?: string | null) => {
      const suffix = String(Date.now()).slice(-6);
      const key: RecordingApiKey = {
        id: `recording-key-${suffix}`,
        name,
        keyPrefix: `pp_demo_${suffix}`,
        secret: `pp_demo_${suffix}_shipping_platform_recording`,
        createdAt: new Date().toISOString(),
        expiresAt: expiresAt ?? null,
        revokedAt: null,
        lastUsedAt: null,
      };
      setApiKeys((current) => [key, ...current]);
      return key;
    },
    [],
  );

  const revokeApiKey = useCallback((id: string) => {
    setApiKeys((current) =>
      current.map((key) =>
        key.id === id ? { ...key, revokedAt: new Date().toISOString() } : key,
      ),
    );
  }, []);

  const rotateApiKey = useCallback(
    (id: string) => {
      const currentKey = apiKeys.find((key) => key.id === id);
      if (!currentKey || currentKey.revokedAt) return null;

      const suffix = String(Date.now()).slice(-6);
      const rotated: RecordingApiKey = {
        ...currentKey,
        keyPrefix: `pp_demo_${suffix}`,
        secret: `pp_demo_${suffix}_shipping_platform_recording`,
        createdAt: new Date().toISOString(),
        revokedAt: null,
        lastUsedAt: null,
      };
      setApiKeys((current) =>
        current.map((key) => (key.id === id ? rotated : key)),
      );
      return rotated;
    },
    [apiKeys],
  );

  const renameProject = useCallback((name: string) => {
    const nextName = name.trim();
    if (nextName) setProjectName(nextName);
  }, []);

  const renameTable = useCallback(
    (tableSlug: string, name: string) => {
      const nextName = name.trim();
      if (!nextName || !preset.tables.some((entry) => entry.slug === tableSlug))
        return;
      setTableNames((current) => ({ ...current, [tableSlug]: nextName }));
    },
    [preset.tables],
  );

  const changeEnvironment = useCallback(
    (nextEnvironmentId: RecordingDemoPresetId) => {
      const nextPreset = getRecordingDemoPreset(nextEnvironmentId);
      clearProcessingTimers();
      clearUploadTimers();
      setEnvironmentId(nextPreset.id);
      setSchemaCreated(!schemaBuilderMode);
      setProjectName(nextPreset.projectName);
      setTableNames(defaultTableNames(nextPreset));
      setFiles([]);
      setRowsByTable({});
      setCustomColumns({});
      setProcessingProgress(0);
    },
    [clearProcessingTimers, clearUploadTimers, schemaBuilderMode],
  );

  const setSchemaBuilderMode = useCallback(
    (enabled: boolean) => {
      clearProcessingTimers();
      clearUploadTimers();
      setSchemaBuilderModeState(enabled);
      setSchemaCreated(!enabled);
      setFiles([]);
      setRowsByTable({});
      setCustomColumns({});
      setProcessingProgress(0);
    },
    [clearProcessingTimers, clearUploadTimers],
  );

  const clearWorkspace = useCallback(() => {
    clearProcessingTimers();
    clearUploadTimers();
    setFiles([]);
    setRowsByTable({});
    setProcessingProgress(0);
  }, [clearProcessingTimers, clearUploadTimers]);

  const reset = useCallback(() => {
    clearProcessingTimers();
    clearUploadTimers();
    setFiles([]);
    setRowsByTable({});
    setCustomColumns({});
    setApiKeys(INITIAL_API_KEYS);
    setInstantModeThreshold(20);
    setProcessingProgress(0);
    setSchemaBuilderModeState(true);
    setSchemaCreated(false);
    setProjectName(preset.projectName);
    setTableNames(defaultTableNames(preset));
    window.localStorage.removeItem(RECORDING_DEMO_STORAGE_KEY);
    window.dispatchEvent(new Event(METADATA_EVENT));
  }, [clearProcessingTimers, clearUploadTimers, preset]);

  useEffect(() => {
    const handleDemoReset = () => reset();
    window.addEventListener(DEMO_ENVIRONMENT_RESET_EVENT, handleDemoReset);
    return () => {
      window.removeEventListener(DEMO_ENVIRONMENT_RESET_EVENT, handleDemoReset);
    };
  }, [reset]);

  const createEmptyMockSchema = useCallback(() => {
    clearProcessingTimers();
    clearUploadTimers();
    setSchemaCreated(true);
    setFiles([]);
    setRowsByTable({});
    setCustomColumns({});
    setProcessingProgress(0);
  }, [clearProcessingTimers, clearUploadTimers]);

  const tables = useMemo(
    () =>
      (schemaBuilderMode && !schemaCreated ? [] : preset.tables).map(
        (table) => ({
          ...table,
          name: tableNames[table.slug] ?? table.name,
        }),
      ),
    [preset.tables, schemaBuilderMode, schemaCreated, tableNames],
  );

  const downloadCsv = useCallback(
    (tableSlug: string) => {
      const table = getTableData(tableSlug);
      if (!table || table.rows.length === 0) return;
      const escape = (value: unknown) =>
        `"${String(value ?? "").replaceAll('"', '""')}"`;
      const csv = [
        table.columns.join(","),
        ...table.rows.map((row) =>
          table.columns.map((column) => escape(row[column])).join(","),
        ),
      ].join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${tableSlug}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    [getTableData],
  );

  const value = useMemo<RecordingDemoContextValue>(
    () => ({
      environmentId,
      schemaBuilderMode,
      schemaCreated,
      preset,
      files,
      rowsByTable,
      customColumns,
      apiKeys,
      instantModeThreshold,
      projectName,
      tableNames,
      tables,
      isProcessing: files.some((file) => file.status === "processing"),
      processingProgress,
      uploadFiles,
      processFiles,
      deleteFiles,
      getTableData,
      addField,
      updateCell,
      createApiKey,
      revokeApiKey,
      rotateApiKey,
      setProcessingThreshold: setInstantModeThreshold,
      renameProject,
      renameTable,
      changeEnvironment,
      setSchemaBuilderMode,
      createEmptyMockSchema,
      clearWorkspace,
      reset,
      downloadCsv,
    }),
    [
      addField,
      apiKeys,
      createApiKey,
      customColumns,
      environmentId,
      schemaBuilderMode,
      schemaCreated,
      deleteFiles,
      downloadCsv,
      files,
      getTableData,
      instantModeThreshold,
      processFiles,
      processingProgress,
      preset,
      projectName,
      tableNames,
      tables,
      renameProject,
      renameTable,
      changeEnvironment,
      setSchemaBuilderMode,
      createEmptyMockSchema,
      clearWorkspace,
      reset,
      revokeApiKey,
      rotateApiKey,
      rowsByTable,
      uploadFiles,
      updateCell,
    ],
  );

  return (
    <RecordingDemoContext.Provider value={value}>
      {children}
    </RecordingDemoContext.Provider>
  );
}

export function useRecordingDataAdapter() {
  const value = useContext(RecordingDemoContext);
  if (!value) {
    throw new Error(
      "useRecordingDataAdapter must be used inside RecordingDataAdapterProvider",
    );
  }
  return value;
}

export function useRecordingDataAdapterOptional() {
  return useContext(RecordingDemoContext);
}

export function useRecordingDemoMetadata() {
  const [metadata, setMetadata] = useState({
    projectName: defaultPreset.projectName,
  });

  useEffect(() => {
    const read = () => {
      try {
        const persisted = window.localStorage.getItem(
          RECORDING_DEMO_STORAGE_KEY,
        );
        if (!persisted) {
          setMetadata({ projectName: defaultPreset.projectName });
          return;
        }
        const state = JSON.parse(persisted) as Partial<PersistedState>;
        const preset = getRecordingDemoPreset(state.environmentId);
        setMetadata({
          projectName: state.projectName?.trim() || preset.projectName,
        });
      } catch {
        setMetadata({ projectName: defaultPreset.projectName });
      }
    };
    read();
    window.addEventListener(METADATA_EVENT, read);
    window.addEventListener("storage", read);
    return () => {
      window.removeEventListener(METADATA_EVENT, read);
      window.removeEventListener("storage", read);
    };
  }, []);

  return metadata;
}
